/**
 * The Generate page's in-progress inputs and queued jobs. In memory, so a navigation away and
 * back restores them; a finished publish or an explicit Cancel resets them.
 */
import { create } from "zustand";
import type { GenerationJobView, GenerationPrintArea } from "@genz/platform";

export interface DraftImage {
  name: string;
  bytes: Uint8Array;
  mime?: string;
}

export interface DraftDetails {
  productType: string;
  brand: string;
  /** The catalog phone model's id. */
  modelId: string;
  /** Sent on the wire as `name`. */
  skuPrefix: string;
  /** The catalog case style's id; optional. */
  caseStyleId: string;
  baseType: string;
  printArea: GenerationPrintArea;
}

/** A queued view job with its view label and the "name · brand · model" sub-line. */
export interface DraftJob {
  id: string;
  label: string;
  job: GenerationJobView;
  details?: string;
}

/** At most this many case references go with one generation. */
export const MAX_CASE_REFERENCES = 5;

interface GenerateDraft {
  /** The case references, in upload order: 1 to `MAX_CASE_REFERENCES`. */
  designs: DraftImage[];
  product?: DraftImage;
  details: DraftDetails;
  /** Selected choice ids per option group key. */
  selected: Record<string, string[]>;
  jobs: DraftJob[];
}

const EMPTY: GenerateDraft = {
  designs: [],
  product: undefined,
  details: { productType: "", brand: "", modelId: "", skuPrefix: "", caseStyleId: "", baseType: "", printArea: "whole" },
  selected: {},
  jobs: [],
};

export const useGenerateDraft = create<GenerateDraft>(() => EMPTY);

export const resetGenerateDraft = () => useGenerateDraft.setState(EMPTY);

/** Published jobs leave the draft; the last one ends the run and its inputs. True when it ended. */
export function dropPublishedJobs(jobIds: string[]): boolean {
  const rest = useGenerateDraft.getState().jobs.filter((j) => !jobIds.includes(j.id));
  if (rest.length > 0) {
    useGenerateDraft.setState({ jobs: rest });
    return false;
  }
  resetGenerateDraft();
  return true;
}
